// 由 scripts/sync-tokens.mjs 自动生成，请勿手改；源文件：supabase/functions/_shared/pptx/tokens.ts
// Design Tokens 单一来源（019_Spec §4 · PPT 视觉升级 V2）
// Deno 渲染器与 React 预览同源引用：前端经 app/scripts/sync-tokens.mjs 构建期复制，
// 禁止任何第二份手工镜像（G8）。改视觉只改这里。
// 色值不带 # 前缀（pptxgenjs 惯例）；前端 sync 脚本统一补 #。

// ---------- 画布（英寸，16:9） ----------
export const CANVAS = { w: 13.333, h: 7.5 } as const;

// ---------- 风格与主题（019 §4.1 注册表，两级选择：风格 → 主题） ----------
export type StyleId = "swiss" | "magazine" | "luxe" | "consult" | "brutal" | "term" | "report" | "ink" | "memphis";
export type Bg = "paper" | "ink" | "accent";

export const SWISS_GREY = {
  paper: "FAFAF8",
  grey1: "F0F0EE",
  grey2: "D4D4D2",
  grey3: "737373",
  ink: "0A0A0A",
} as const;

export interface SwissTheme {
  id: string;
  style: "swiss";
  name: string;
  suitable: string;
  accent: string;
  /** accent 底色上的文字色（浅底高饱和 accent 上禁白字，019 §4.1） */
  accentOn: string;
  /** accentOn 文字是否须 Bold（swiss-orange 规则） */
  accentOnBold: boolean;
  grey: typeof SWISS_GREY;
}

export interface MagTheme {
  id: string;
  style: "magazine";
  name: string;
  suitable: string;
  ink: string;
  paper: string;
  paperTint: string;
}

/** 暗夜轻奢（039_Spec §4.1）：墨黑×裸金双色纪律 + 衬线标题 + 装饰圆几何 */
export interface LuxeTheme {
  id: string;
  style: "luxe";
  name: string;
  suitable: string;
  /** 内容页米白底 */
  paper: string;
  /** 深底页正文黑 */
  ink: string;
  /** 封面/章节满屏墨黑 */
  inkDeep: string;
  /** 裸金锚点（装饰圆/金条/关键数字） */
  accent: string;
  accentDeep: string;
  accentLight: string;
  /** 浅金辅助面（卡片/分区底） */
  blush: string;
  /** hairline / 次要文字 */
  grey1: string;
  grey2: string;
  /** 裸金上的文字色（金底白字对比度不足，禁用白——用墨黑） */
  accentOn: string;
  /** 风格级装饰开关（039 §4.1 新增字段） */
  decorCircle: true;
  titleSerif: true;
}

/** 咨询蓝（043_Spec §4.2 · 来源 consulting）：净白底 + navy 结构 + 语义 4 色契约（永不改派） */
export interface ConsultTheme {
  id: string;
  style: "consult";
  name: string;
  suitable: string;
  /** 净白底 */
  paper: string;
  /** navy 正文/标题（= 结构色 accents[0]，兼作 Skin.accent 锚点） */
  ink: string;
  /** 次要文字 */
  muted: string;
  /** 结构主色（= ink navy） */
  accent: string;
  /** navy 上的文字色 */
  accentOn: string;
  /** 语义色：好·机会 */
  accentEmerald: string;
  /** 语义色：品牌·高亮 */
  accentGold: string;
  /** 语义色：风险·差距 */
  accentCoral: string;
  /** 语义色：中性 */
  accentSlate: string;
}

/** 粗野报纸（043_Spec §4.3 · 来源 brutalist）：白/黑/唯一红 + 粗黑线（ruleW=3.0）+ 零圆角 */
export interface BrutalTheme {
  id: string;
  style: "brutal";
  name: string;
  suitable: string;
  /** 白报纸底 */
  paper: string;
  /** 墨黑（粗黑线/标题/正文） */
  ink: string;
  /** 次要文字（深灰，不用浅灰——报纸无暧昧） */
  muted: string;
  /** 唯一红（每页至多出现一次） */
  accent: string;
  /** 红底上的文字色 */
  accentOn: string;
}

/** 终端磷光（043_Spec §4.4 · 来源 terminal）：近黑底 + 磷光绿 + 全等宽；ink/paper 语义反转（无浅底页） */
export interface TermTheme {
  id: string;
  style: "term";
  name: string;
  suitable: string;
  /** CRT 近黑底（内容页页底） */
  paper: string;
  /** 磷光浅绿正文 */
  ink: string;
  /** 暗绿次要文字 */
  muted: string;
  /** 纯黑满屏（封面/章节，更高对比） */
  inkDeep: string;
  /** 磷光绿（唯一主 accent；仅文字/线条，禁绿底满屏） */
  accent: string;
  /** 磷光绿上的文字色（近黑） */
  accentOn: string;
  /** 状态色：warn（琥珀） */
  accentAmber: string;
  /** 状态色：info（蓝） */
  accentBlue: string;
  /** 状态色：error（红） */
  accentRed: string;
}

/** 研报深墨（043_Spec §4.5 · 来源 editorial_report）：FT 式近黑 + 琥珀主高亮 + 一红 + 衬线大标题 */
export interface ReportTheme {
  id: string;
  style: "report";
  name: string;
  suitable: string;
  /** FT 式近黑底（全风格页底） */
  paper: string;
  /** 近白正文 */
  ink: string;
  /** 次要文字 */
  muted: string;
  /** 纯黑满屏（封面/章节） */
  inkDeep: string;
  /** 琥珀（品牌/次级高亮，仅小面积文字/线） */
  accent: string;
  /** 琥珀上的文字色（近黑） */
  accentOn: string;
  /** 一红（每页至多一处强调） */
  accentRed: string;
}

/** 水墨朱印（043_Spec §4.6 · 来源 ink_wash）：暖纸 + 墨黑 + 朱红印章 + 楷体 + 大留白 */
export interface InkTheme {
  id: string;
  style: "ink";
  name: string;
  suitable: string;
  /** 暖纸底 */
  paper: string;
  /** 墨黑正文/标题 */
  ink: string;
  /** 灰墨次要文字 */
  muted: string;
  /** 朱红（只做印章/关键字，绝不大面积填充） */
  accent: string;
  /** 印章内文字色（白） */
  accentOn: string;
  /** 黛灰（次级 accent） */
  accentGrey: string;
}

/** 孟菲斯（043_Spec §4.7 · 来源 memphis）：奶油底 + 5 高饱和轮用 + 大圆角卡 + 边距几何母题 */
export interface MemphisTheme {
  id: string;
  style: "memphis";
  name: string;
  suitable: string;
  /** 奶油底（每个内容页必须保持） */
  paper: string;
  /** 近黑正文 */
  ink: string;
  /** 次要文字 */
  muted: string;
  /** 橙（5 色轮用锚点；本风格豁免于「单一 accent」，043 §4.7） */
  accent: string;
  /** 头带文字色按明度自动黑白（043 §4.7）；此处给深色带的通用白 */
  accentOn: string;
  accentBlue: string;
  accentRed: string;
  accentGreen: string;
  accentYellow: string;
}

export type ThemeDef = SwissTheme | MagTheme | LuxeTheme
  | ConsultTheme | BrutalTheme | TermTheme | ReportTheme | InkTheme | MemphisTheme;

const swiss = (id: string, name: string, accent: string, accentOn: string, accentOnBold: boolean, suitable: string): SwissTheme =>
  ({ id, style: "swiss", name, suitable, accent, accentOn, accentOnBold, grey: SWISS_GREY });

export const THEMES: Record<string, ThemeDef> = {
  "swiss-ikb":    swiss("swiss-ikb",    "克莱因蓝", "002FA7", "FFFFFF", false, "默认；科技 / 商业发布 / 通用"),
  "swiss-lemon":  swiss("swiss-lemon",  "柠檬黄",   "FFD500", "0A0A0A", false, "年轻 / 零售 / 消费品"),
  "swiss-green":  swiss("swiss-green",  "柠檬绿",   "C5E803", "0A0A0A", false, "生态 / 未来 / Z 世代"),
  "swiss-orange": swiss("swiss-orange", "安全橙",   "FF6B35", "FFFFFF", true,  "工业 / 警示 / 汽车"),
  "mag-classic": { id: "mag-classic", style: "magazine", name: "墨水经典", ink: "0A0A0B", paper: "F1EFEA", paperTint: "E8E5DE", suitable: "默认；通用 / 商业" },
  "mag-indigo":  { id: "mag-indigo",  style: "magazine", name: "靛蓝瓷",   ink: "0A1F3D", paper: "F1F3F5", paperTint: "E4E8EC", suitable: "科技 / 研究 / 数据" },
  "mag-forest":  { id: "mag-forest",  style: "magazine", name: "森林墨",   ink: "1A2E1F", paper: "F5F1E8", paperTint: "ECE7DA", suitable: "自然 / 文化 / 可持续" },
  "mag-kraft":   { id: "mag-kraft",   style: "magazine", name: "牛皮纸",   ink: "2A1E13", paper: "EEDFC7", paperTint: "E0D0B6", suitable: "人文 / 怀旧 / 手作" },
  "mag-dune":    { id: "mag-dune",    style: "magazine", name: "沙丘",     ink: "1F1A14", paper: "F0E6D2", paperTint: "E3D7BF", suitable: "设计 / 艺术 / 创意" },
  "luxe-noir": {
    id: "luxe-noir", style: "luxe", name: "暗夜轻奢",
    suitable: "高端护肤 / 发布会 / 品牌升级提案",
    paper: "F5F1EC", ink: "1A1A1A", inkDeep: "0D0D0D",
    accent: "C4937A", accentDeep: "B8956A", accentLight: "D4A896", blush: "EDD5CA",
    grey1: "BBBBBB", grey2: "888888", accentOn: "1A1A1A",
    decorCircle: true, titleSerif: true,
  },
  // ---- 主题库扩展第一批（043_Spec §4.2–4.7，色值逐字抄自 slides_maker presets.py）----
  "consult-navy": {
    id: "consult-navy", style: "consult", name: "咨询蓝",
    suitable: "商务咨询 / 战略提案 / 董事会汇报",
    paper: "FFFFFF", ink: "1A2B45", muted: "5A6B82",
    accent: "1A2B45", accentOn: "FFFFFF",
    accentEmerald: "1F8A70", accentGold: "C9A227", accentCoral: "C0492E", accentSlate: "64748B",
  },
  "brutal-paper": {
    id: "brutal-paper", style: "brutal", name: "粗野报纸",
    suitable: "强态度品牌 / 年轻化宣言 / 报纸风",
    paper: "FFFFFF", ink: "111111", muted: "3A3A3A",
    accent: "C8102E", accentOn: "FFFFFF",
  },
  "term-phosphor": {
    id: "term-phosphor", style: "term", name: "终端磷光",
    suitable: "极客 / 开发者产品 / 技术发布",
    paper: "0A0F0A", ink: "C8F0C8", muted: "5A825A", inkDeep: "000000",
    accent: "33FF66", accentOn: "0A0F0A",
    accentAmber: "FFB000", accentBlue: "5AC8FF", accentRed: "FF5A5A",
  },
  "report-ft": {
    id: "report-ft", style: "report", name: "研报深墨",
    suitable: "数据研报 / 投研路演 / 深色金融",
    paper: "0E0E12", ink: "ECECEF", muted: "8C8C97", inkDeep: "000000",
    accent: "D9A441", accentOn: "0E0E12", accentRed: "E0392B",
  },
  "ink-zhu": {
    id: "ink-zhu", style: "ink", name: "水墨朱印",
    suitable: "中式品牌 / 文化 / 茶饮 / 国风",
    paper: "F5F1E8", ink: "1A1A1A", muted: "8B8680",
    accent: "A52A2A", accentOn: "FFFFFF", accentGrey: "5C5852",
  },
  "memphis-pop": {
    id: "memphis-pop", style: "memphis", name: "孟菲斯",
    suitable: "年轻消费 / 活动营销 / 玩味 campaign",
    paper: "FCF2D8", ink: "1A1A17", muted: "9A9384",
    accent: "F76302", accentOn: "FFFFFF",
    accentBlue: "0548C5", accentRed: "E2342B", accentGreen: "1B7A3D", accentYellow: "F6BE1A",
  },
};

export const DEFAULT_THEME_ID = "swiss-ikb";

export function getTheme(themeId: string | null | undefined): ThemeDef {
  return THEMES[themeId ?? DEFAULT_THEME_ID] ?? THEMES[DEFAULT_THEME_ID];
}

// ---------- 字体（019 §4.2 + 字体风险节） ----------
// 注意：pptxgenjs 的 fontFace 只能写单个字体名，无法写备选链——
// pptx 打开时由 PowerPoint 自行替换缺失字体；stack 数组供前端 CSS 与 lint/文档使用，
// primary 为写入 .pptx 的值。
export interface FontSlot {
  primary: string;
  stack: string[];
}

export const FONT = {
  swiss: {
    /** 大标题：微软雅黑 Light（Win8+ 安全；缺失降级雅黑家族，可接受） */
    headLight: { primary: "Microsoft YaHei Light", stack: ["Microsoft YaHei Light", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    body:      { primary: "Microsoft YaHei",       stack: ["Microsoft YaHei", "PingFang SC", "sans-serif"] },
    /** Data Hero 英数：Segoe UI Light */
    dataHero:  { primary: "Segoe UI Light",        stack: ["Segoe UI Light", "Microsoft YaHei Light", "sans-serif"] },
    /** kicker/meta/页码：英 Consolas + 中 微软雅黑 Bold，全大写字距加宽 */
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  magazine: {
    /** 衬线标题：Noto Serif SC → 华文中宋 → 宋体（pptx 内写 primary，导出页提示安装思源） */
    serif:     { primary: "Noto Serif SC",         stack: ["Noto Serif SC", "STZhongsong", "SimSun", "serif"] },
    body:      { primary: "Microsoft YaHei",       stack: ["Microsoft YaHei", "PingFang SC", "sans-serif"] },
    /** Data Hero 英数衬线 */
    dataHero:  { primary: "Georgia",               stack: ["Georgia", "Noto Serif SC", "serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  luxe: {
    /** 衬线标题（轻奢识别特征：衬线西文 + 中文衬线链） */
    serif:     { primary: "Noto Serif SC",         stack: ["Noto Serif SC", "STZhongsong", "SimSun", "serif"] },
    body:      { primary: "Microsoft YaHei",       stack: ["Microsoft YaHei", "PingFang SC", "sans-serif"] },
    /** Data Hero 英数：Cambria（参考件实测标题字体） */
    dataHero:  { primary: "Cambria",               stack: ["Cambria", "Georgia", "Noto Serif SC", "serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  // ---- 主题库扩展第一批（043_Spec §4.2–4.7 字体链；primary 写入 pptx，CJK 由 PowerPoint 替换）----
  consult: {
    /** 标题：Arial（Office 全安全）；中文 presets 原生指定雅黑（栈内第二顺位） */
    head:      { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    body:      { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    dataHero:  { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "sans-serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  brutal: {
    /** 标题：Arial Black（Windows/Office 自带，全大写紧缩）；中文黑体兜底（雅黑 Bold） */
    head:      { primary: "Arial Black",           stack: ["Arial Black", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    body:      { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    /** 巨大生数字同 Arial Black */
    dataHero:  { primary: "Arial Black",           stack: ["Arial Black", "Microsoft YaHei", "sans-serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  term: {
    /** 全 Consolas 等宽（标题/正文/数字一律）；中文雅黑兜底（等宽无中文，接受混排） */
    head:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
    body:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
    dataHero:  { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  report: {
    /** 标题：Georgia（Office 自带）+ 中文衬线链（Noto Serif SC→华文中宋→宋体） */
    head:      { primary: "Georgia",               stack: ["Georgia", "Noto Serif SC", "STZhongsong", "SimSun", "serif"] },
    body:      { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    dataHero:  { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  ink: {
    /** 标题：楷体（KaiTi，Windows 自带；macOS 为 Kaiti SC——pptx 内写 KaiTi，跨机由 PowerPoint 替换）+ 英文 Georgia 兜底 */
    head:      { primary: "KaiTi",                 stack: ["KaiTi", "Kaiti SC", "STKaiti", "Georgia", "serif"] },
    body:      { primary: "Microsoft YaHei",       stack: ["Microsoft YaHei", "SimSun", "PingFang SC", "sans-serif"] },
    dataHero:  { primary: "Georgia",               stack: ["Georgia", "KaiTi", "serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
  memphis: {
    /** 标题：Arial Black；中文雅黑 Bold 兜底 */
    head:      { primary: "Arial Black",           stack: ["Arial Black", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    body:      { primary: "Arial",                 stack: ["Arial", "Microsoft YaHei", "PingFang SC", "sans-serif"] },
    dataHero:  { primary: "Arial Black",           stack: ["Arial Black", "Microsoft YaHei", "sans-serif"] },
    mono:      { primary: "Consolas",              stack: ["Consolas", "Microsoft YaHei", "monospace"] },
  },
} as const;

// ---------- 字号阶梯（pt，019 §4.2；区间取中档，标题长度分档由渲染器/lint 处理） ----------
export interface TypeScale {
  coverTitle: number;   // 封面主标题
  pageTitle: number;    // 页面大标题
  dataHero: number;     // Data Hero 巨号数字
  sectionHead: number;  // 小节/卡片标题
  body: number;         // 正文/要点
  caption: number;      // 图注/卡片描述
  meta: number;         // kicker/meta/页码
}

export const TYPE_SCALE: Record<StyleId, TypeScale> = {
  swiss:    { coverTitle: 60, pageTitle: 36, dataHero: 70, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  magazine: { coverTitle: 48, pageTitle: 33, dataHero: 63, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  luxe:     { coverTitle: 52, pageTitle: 34, dataHero: 66, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  // ---- 主题库扩展第一批（043 未给阶梯——沿用 019 §4.2 框架与下限：正文 ≥14 / 图注 ≥12 / meta ≥9.5）----
  consult:  { coverTitle: 48, pageTitle: 32, dataHero: 60, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  brutal:   { coverTitle: 60, pageTitle: 38, dataHero: 76, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  term:     { coverTitle: 44, pageTitle: 30, dataHero: 58, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  report:   { coverTitle: 50, pageTitle: 33, dataHero: 64, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  ink:      { coverTitle: 54, pageTitle: 34, dataHero: 60, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
  memphis:  { coverTitle: 56, pageTitle: 36, dataHero: 70, sectionHead: 18, body: 14.5, caption: 12, meta: 9.5 },
};

/** lint 可读性下限（019 §3 第 9 条 / §4.2） */
export const MIN_FONT = { body: 14, caption: 12, meta: 9 } as const;

/** 中文标题长度分档（019 §4.2）：≤8 字满档；9–12 字降一档；>12 字要求改写而非缩字 */
export const TITLE_LEN = { full: 8, degrade: 12 } as const;

// ---------- 间距与网格（019 §4.5，英寸） ----------
export const SPACING = {
  marginX: 0.67,        // 内容区左右边距（≈5vw）
  titleToContent: 0.7,  // 标题区到内容区最小间距
  bottomSafe: 0.4,      // 底部安全区（不放任何内容）
  hairline: 0.0104,     // 1px @96dpi ≈ 0.0104in
  cardGap: 0.25,        // 卡片间距
  chromeTop: 0.32,      // kicker/页码 y
  chromeFootY: 7.08,    // 底部 foot 行 y
} as const;

// ---------- 版式库 V2 注册（019 §4.3；KBS 032 §4 扩至 17 种） ----------
export const LAYOUT_V2 = [
  "cover", "divider", "statement", "kpi-hero", "bar-chart", "duo-compare",
  "timeline-h", "timeline-v", "cards", "matrix", "ledger", "image-text", "closing",
  // KBS 新增 4 版式（感知图/网格矩阵/金字塔/网格表）
  "perceptual-map", "quad-matrix", "pyramid", "grid-table",
  // 主题库扩展 v1.1 数据组件 5 版式（043 §4.10）
  "waterfall", "gantt", "funnel", "dumbbell", "bullet",
] as const;
export type LayoutV2 = (typeof LAYOUT_V2)[number];

export const LAYOUT_V2_LABEL: Record<LayoutV2, string> = {
  "cover": "封面", "divider": "章节", "statement": "金句", "kpi-hero": "巨数",
  "bar-chart": "条形图", "duo-compare": "对比", "timeline-h": "横向流程",
  "timeline-v": "纵向时间线", "cards": "卡片组", "matrix": "矩阵",
  "ledger": "账单", "image-text": "图文", "closing": "收尾",
  "perceptual-map": "感知图", "quad-matrix": "网格矩阵", "pyramid": "金字塔", "grid-table": "网格表",
  "waterfall": "瀑布桥", "gantt": "排期泳道", "funnel": "转化漏斗", "dumbbell": "对比记分板", "bullet": "目标带",
};

/** 数字版式：lint 要求必须有真实数字（019 §4.4；grid-table 常承载测算数字）
 *  v1.1 数据组件（043 §4.10）：waterfall/funnel/dumbbell/bullet 为数字版式；
 *  gantt 排期段是计划性内容（LLM 可写），不计入 NUMERIC_NO_DATA 口径 */
export const NUMERIC_LAYOUTS: LayoutV2[] = [
  "kpi-hero", "bar-chart", "ledger", "grid-table",
  "waterfall", "funnel", "dumbbell", "bullet",
];

/** 旧 10 枚举 → V2 映射（019 §4.3 表；review/editor 缺省时按此推导） */
export const LEGACY_LAYOUT_MAP: Record<string, LayoutV2> = {
  cover: "cover",
  concept: "statement",
  vision: "closing",
  data: "kpi-hero",
  insight: "duo-compare",
  strategy: "cards",
  channel: "matrix",
  roadmap: "timeline-h",
  financial: "ledger",
  risk: "cards",
};

// ---------- 节奏与多样性硬规则（019 §4.4，lint/outline 共用） ----------
export const RHYTHM = {
  minDistinctLayouts: 8,   // 12 页至少 8 种版式
  maxSameLayoutRun: 2,     // 不允许连续 3 页同版式（即连续同版式 ≤2）
  maxSameBgRun: 3,         // 底色节奏：每 3 页内至少一次明暗切换（连续第 4 页同底色才判不合格，
                           // 调和 019 §4.4 硬规则与「浅底正文 ×2-3」示例——规格已同步修订）
  defaultPages: 12,
} as const;

/** 源文件色值不带 # 前缀（pptxgenjs 惯例），前端 CSS 用时统一补 # */
export function hex(c: string): string { return "#" + c; }
